import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import {
  ResultsShareButton,
  resultsShareOf,
  type ResultsPdfShare,
  type ResultsShare,
} from '../../shared/results_page/results_share.js';
import {
  ResultsDateChips,
  type DateChipView,
} from '../../shared/results_page/results_date_chips.js';
import {
  ResultsFilteredEmpty,
  type ResultsFilteredEmptyCopy,
} from '../../shared/results_page/results_filtered_empty.js';
import {
  ResultsLayout,
  type ResultsLayoutProps,
} from '../../shared/results_page/results_layout.js';
import {
  decisionNotice,
  type ResultsNoticeView,
} from '../../shared/results_page/results_notice.js';
import {
  ResultsOfflineRequest,
  type ResultsOfflineRequestProps,
} from '../../shared/results_page/results_offline_request.js';
import type { BusLegStepView, BusOfferView } from '../models.js';
import type { BusSeatLayout } from '../seats/bus_seat_picker.js';
import { BusLegStepper, type BusLegStepperCopy } from './bus_leg_stepper.js';
import type { BusOfferCardCopy } from './bus_offer_card.js';
import { BusResultsFilterChips, type BusResultsChipsCopy } from './bus_results_filter_chips.js';
import { BusResultsFilters, type BusResultsFiltersCopy } from './bus_results_filters.js';
import { BusResultsFiltersSheet } from './bus_results_filters_sheet.js';
import { BusResultsList } from './bus_results_list.js';
import {
  activeBusFilterCount,
  EMPTY_BUS_RESULTS_FILTERS,
  matchesBusResultsFilters,
  sortBusOffers,
  type BusFilterableOffer,
  type BusResultsFilterValue,
  type BusResultsOrder,
} from './bus_results_rules.js';
import { BusResultsSort, type BusResultsSortCopy } from './bus_results_sort.js';
import { BUS_SEAT_SHEET_QUERY, BusSeatSheet } from './bus_seat_sheet.js';

export type BusResultsPageCopy = {
  /** O título da perna, já formatado pelo host — "Ida — Jabaquara, SP · Seg, 25 ago". */
  title: string;
  /** O contador ao lado do título — "0 de 2 escolhidos". */
  chosenLabel: string;
  /** O compartilhar da busca, ao lado do título. Só aparece com `onShare`. */
  shareLabel: string;
  totalLabel(total: number): string;
  visibleLabel(visible: number, total: number): string;
  /** O nome acessível da lista ("Ônibus encontrados"). */
  resultsLabel: string;
  /** O botão do pé da folha de filtros do celular ("Aplicar filtros"). */
  applyFiltersLabel: string;
  loadMoreLabel: string;
  /** O nome acessível da fileira de datas ("Trocar a data"). */
  datesLabel: string;
  filteredEmpty: ResultsFilteredEmptyCopy;
  sort: BusResultsSortCopy;
  /** A fila de chips do palco estreito. Só faz falta com `chipsContainer`. */
  chips?: BusResultsChipsCopy;
  card: BusOfferCardCopy;
  legs: BusLegStepperCopy;
  /** O título da folha da escolha de poltrona no celular e no tablet. */
  seatsTitle: string;
  /** O nome acessível do voltar da folha ("Fechar escolha de poltrona"). */
  seatsCloseLabel: string;
};

/** As pernas da viagem e o gesto de voltar a uma já escolhida. */
export type BusResultsLegs = {
  readonly steps: readonly BusLegStepView[];
  readonly onStepSelect?: (index: number) => void;
};

/** A fileira de datas da perna em cena. */
export type BusResultsDates = {
  readonly days: readonly DateChipView[];
  readonly selected: string;
  readonly onSelect: (date: string) => void;
};

export type BusResultsPageProps = {
  offers: readonly BusFilterableOffer[];
  currency: string;
  copy: BusResultsPageCopy;
  filtersCopy: BusResultsFiltersCopy;
  /** Controlados no chamador: trocar de perna ou de dia preserva o corte. */
  filters: BusResultsFilterValue;
  order: BusResultsOrder;
  legs: BusResultsLegs;
  dates: BusResultsDates;
  selectedOfferId?: string;
  /** O card aberto na escolha de poltrona e o que abre nele (#2440, bloco 3)
   *  — inline no card, ou na folha abaixo de `BUS_SEAT_SHEET_QUERY`. */
  expandedOfferId?: string;
  renderExpansion?: (offerId: string, layout: BusSeatLayout) => ReactNode;
  /** Fechar a folha da escolha (o voltar, o arrasto ou o Esc). */
  onCollapseExpansion?: () => void;
  /** Uma busca ou uma escrita em voo: cortes, datas e `Selecionar` travam. */
  locked?: boolean;
  /** O desfecho de uma busca SEM viagem (dia vazio ou falha), no corpo da
   *  lista: a página fica — pernas, datas e cortes —, como no aéreo. Sem ele, a
   *  lista vazia é o vazio-por-corte. */
  outcome?: ReactNode;
  /** O pé da lista que oferece o pedido OFFLINE à equipe VOLL
   *  (`ResultsOfflineRequest`): copy e envio são do host. Fica sob a lista e
   *  sob o vazio-por-corte, como no hotel; some com o desfecho de busca sem
   *  viagem e enquanto a busca corre — oferecer a saída antes de a busca
   *  terminar é desistir por ela. */
  offlineRequest?: ResultsOfflineRequestProps;
  notice?: ResultsNoticeView;
  /** Quem rola a lista na linha (`ResultsLayout`): o host (o padrão) ou a
   *  própria página, quando o host lhe dá altura travada (`own`, #4587). */
  listScroll?: ResultsLayoutProps['listScroll'];
  /**
   * Onde a fila de chips de filtro/ordenação monta no palco ESTREITO — a
   * segunda linha da top bar do host, como no hotel e no carro (#3140). Com o
   * slot (e `copy.chips`), a linha da meta fica só com a contagem. Ausente ⇒
   * `Filtros` e o ordenador seguem na linha da meta.
   */
  chipsContainer?: Element | null;
  onQueryChange(query: { filters: BusResultsFilterValue; order: BusResultsOrder }): void;
  /** O `Detalhes` de uma viagem abriu (ver `BusOfferCardProps.onDetailsOpen`). */
  onDetailsOpen?: (offerId: string) => void;
  onSelect(offerId: string): void;
  /** O compartilhar da busca (#5156), ao lado do título — como no carro. */
  onShare?: () => void;
  /** O "Baixar PDF" do compartilhar: com ele o botão vira o menu, e o PDF recebe
   *  as viagens da perna em cena, na ordem e com os filtros da tela. */
  pdfShare?: ResultsPdfShare<BusOfferView>;
};

/** O compartilhar (#5156) e o contador de escolhas ao lado do título. */
function titleActionsOf(share: ResultsShare | undefined, chosenLabel: string): ReactNode {
  return (
    <>
      {share && <ResultsShareButton share={share} />}
      <span className="text-caption text-muted-foreground">{chosenLabel}</span>
    </>
  );
}

/** A view da viagem aberta na escolha de poltrona (ou nada). */
function offerViewOf(
  offers: readonly BusFilterableOffer[],
  offerId: string | undefined,
): BusOfferView | undefined {
  return offers.find((candidate) => candidate.offer.id === offerId)?.offer;
}

/** O corte e a ordem, memoizados — array novo por render remontaria a lista. */
function useBusPageOffers(
  offers: readonly BusFilterableOffer[],
  filters: BusResultsFilterValue,
  order: BusResultsOrder,
) {
  return useMemo(
    () =>
      sortBusOffers(
        offers.filter((facts) => matchesBusResultsFilters(facts, filters)),
        order,
      ).map(({ offer }) => offer),
    [filters, offers, order],
  );
}

/** Só um botão com foco (o `Selecionar`): um toque no iOS não foca, e aí o
 *  foco fica onde estiver em vez de pousar num alvo qualquer. */
function focusedButton(): HTMLButtonElement | null {
  return document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
}

/** Quanto dura o sinal de corte aplicado: perceptível, sem parecer espera. */
const CUT_SIGNAL_MS = 350;

/** O corte (filtros e ordem) acabou de mudar (#6421)? Ele roda no cliente, num
 *  só commit e sem requisição: sem este sinal a lista troca em silêncio e a
 *  pessoa procura um "Aplicar" que não existe. */
function useCutSignal(
  filters: BusResultsFilterValue,
  order: BusResultsOrder,
): { cut: string; refreshing: boolean } {
  const cut = JSON.stringify({ filters, order });
  const [seen, setSeen] = useState(cut);
  // The cut being signalled: a new one restarts the signal's clock.
  const [signal, setSignal] = useState<string | null>(null);
  if (seen !== cut) {
    setSeen(cut);
    setSignal(cut);
  }
  useEffect(() => {
    if (signal === null) return undefined;
    const timer = setTimeout(() => {
      setSignal(null);
    }, CUT_SIGNAL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [signal]);
  return { cut, refreshing: signal !== null };
}

/** A linha da meta: a contagem, ou a espera enquanto a busca corre. */
function metaLabel(
  copy: BusResultsPageCopy,
  visible: number,
  total: number,
  notice: ResultsNoticeView | undefined,
): string {
  if (notice?.tone === 'loading') return notice.title;
  return visible === total ? copy.totalLabel(total) : copy.visibleLabel(visible, total);
}

/** A fila de chips na segunda linha da barra do host — só no palco estreito,
 *  com o slot aberto e a copy deles. Com ela, a linha da meta fica só com a
 *  contagem (`metaControls: 'none'`). */
function chipsOf(
  narrow: boolean,
  container: Element | null | undefined,
  page: Pick<
    BusResultsPageProps,
    'offers' | 'currency' | 'filters' | 'order' | 'filtersCopy' | 'copy' | 'onQueryChange'
  > & { readonly locked: boolean },
): { portal: ReactNode; metaControls: 'inline' | 'none' } {
  const copy = narrow ? page.copy.chips : undefined;
  if (!container || !copy) return { portal: null, metaControls: 'inline' };
  return {
    portal: createPortal(
      <BusResultsFilterChips
        offers={page.offers}
        currency={page.currency}
        filters={page.filters}
        order={page.order}
        filtersCopy={page.filtersCopy}
        sortCopy={page.copy.sort}
        copy={copy}
        applyLabel={page.copy.applyFiltersLabel}
        locked={page.locked}
        onQueryChange={page.onQueryChange}
      />,
      container,
    ),
    metaControls: 'none',
  };
}

/** O pé da lista com a porta do pedido à equipe VOLL — fora enquanto a busca
 *  corre: oferecer a saída antes de a busca terminar é desistir por ela. */
function OfflineFoot({
  offlineRequest,
  pending,
}: {
  readonly offlineRequest: ResultsOfflineRequestProps;
  readonly pending: boolean;
}): ReactNode {
  if (pending) return null;
  return (
    <div className="mt-4">
      <ResultsOfflineRequest {...offlineRequest} />
    </div>
  );
}

/** A faixa entre o título e a meta: o stepper de pernas e a fileira de datas. */
function LegsBanner({
  legs,
  dates,
  copy,
  locked,
}: {
  readonly legs: BusResultsLegs;
  readonly dates: BusResultsDates;
  readonly copy: BusResultsPageCopy;
  readonly locked: boolean;
}): ReactNode {
  return (
    <div className="grid gap-3">
      <BusLegStepper
        steps={legs.steps}
        copy={copy.legs}
        disabled={locked}
        {...(legs.onStepSelect ? { onStepSelect: legs.onStepSelect } : {})}
      />
      <ResultsDateChips
        days={dates.days}
        selected={dates.selected}
        label={copy.datesLabel}
        disabled={locked}
        onSelect={dates.onSelect}
      />
    </div>
  );
}

/**
 * A disponibilidade DETERMINÍSTICA de ônibus (#2440), uma perna por vez — a
 * convenção da disponibilidade do aéreo (#2363): título com contador, stepper
 * de pernas e a fileira de datas (D-2 … D+2) na faixa, a ordenação na linha da
 * meta, o painel de cortes da #2351 e os cards. A escolha de poltrona abre
 * INLINE no card (`renderExpansion`) — ou, no celular e no tablet, em tela
 * cheia com a planta em pé (`BUS_SEAT_SHEET_QUERY`).
 */
export function BusResultsPage({
  offers,
  currency,
  copy,
  filtersCopy,
  filters,
  order,
  legs,
  dates,
  selectedOfferId,
  expandedOfferId,
  renderExpansion,
  onCollapseExpansion,
  locked = false,
  outcome,
  offlineRequest,
  notice,
  onQueryChange,
  onDetailsOpen,
  onSelect,
  listScroll,
  chipsContainer,
  onShare,
  pdfShare,
}: Readonly<BusResultsPageProps>): ReactNode {
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  const sheet = useMediaQuery(BUS_SEAT_SHEET_QUERY);
  // Quem abriu a folha recebe o foco de volta: o `Selecionar` do card.
  const trigger = useRef<HTMLButtonElement | null>(null);
  const views = useBusPageOffers(offers, filters, order);
  const { cut, refreshing } = useCutSignal(filters, order);
  const applyFilters = (next: BusResultsFilterValue) => {
    onQueryChange({ filters: next, order });
  };
  const renderFiltersSheet: ResultsLayoutProps['filtersSheet'] = (slot) => (
    <BusResultsFiltersSheet
      {...slot}
      offers={offers}
      currency={currency}
      value={filters}
      copy={filtersCopy}
      applyLabel={copy.applyFiltersLabel}
      locked={locked}
      onApply={applyFilters}
    />
  );
  const cardNotice = decisionNotice(notice);
  const page = { offers, currency, filters, order, filtersCopy, copy, locked, onQueryChange };
  const chips = chipsOf(narrow, chipsContainer, page);
  return (
    <>
      {chips.portal}
      <ResultsLayout
        copy={{
          filtersLabel: filtersCopy.filtersLabel,
          resetFiltersLabel: filtersCopy.resetFiltersLabel,
        }}
        narrow={narrow}
        listScroll={listScroll}
        metaControls={chips.metaControls}
        title={copy.title}
        titleActions={titleActionsOf(
          resultsShareOf(copy.shareLabel, onShare, pdfShare, views),
          copy.chosenLabel,
        )}
        banner={<LegsBanner legs={legs} dates={dates} copy={copy} locked={locked} />}
        meta={metaLabel(copy, views.length, offers.length, notice)}
        metaActions={
          <BusResultsSort
            value={order}
            copy={copy.sort}
            disabled={locked}
            onChange={(next) => {
              onQueryChange({ filters, order: next });
            }}
          />
        }
        activeFilterCount={activeBusFilterCount(filters)}
        onResetFilters={() => {
          applyFilters(EMPTY_BUS_RESULTS_FILTERS);
        }}
        filters={
          <BusResultsFilters
            offers={offers}
            currency={currency}
            value={filters}
            copy={filtersCopy}
            locked={locked}
            frame={false}
            header={false}
            onChange={applyFilters}
          />
        }
        filtersSheet={renderFiltersSheet}
        content={
          <>
            <BusResultsList
              resetKey={cut}
              offers={views}
              resultsLabel={copy.resultsLabel}
              cardCopy={copy.card}
              loadMoreLabel={copy.loadMoreLabel}
              {...(selectedOfferId ? { selectedOfferId } : {})}
              {...(expandedOfferId ? { expandedOfferId } : {})}
              {...(renderExpansion && !sheet
                ? { renderExpansion: (offerId: string) => renderExpansion(offerId, 'inline') }
                : {})}
              busy={locked}
              {...(cardNotice ? { notice: cardNotice } : {})}
              pending={notice?.tone === 'loading'}
              refreshing={refreshing}
              onDetailsOpen={onDetailsOpen}
              empty={
                outcome ?? (
                  <ResultsFilteredEmpty
                    copy={copy.filteredEmpty}
                    {...(locked
                      ? {}
                      : {
                          onClear: () => {
                            applyFilters(EMPTY_BUS_RESULTS_FILTERS);
                          },
                        })}
                  />
                )
              }
              onSelect={(offerId) => {
                trigger.current = focusedButton();
                onSelect(offerId);
              }}
            />
            {offlineRequest && !outcome && (
              <OfflineFoot offlineRequest={offlineRequest} pending={notice?.tone === 'loading'} />
            )}
          </>
        }
      />
      {renderExpansion && sheet && (
        <BusSeatSheet
          offer={offerViewOf(offers, expandedOfferId)}
          title={copy.seatsTitle}
          closeLabel={copy.seatsCloseLabel}
          renderExpansion={renderExpansion}
          returnFocusTo={trigger}
          onClose={() => onCollapseExpansion?.()}
        />
      )}
    </>
  );
}
